import React from 'react';
import { Card, Table, Tabs, Progress, Tag, Spin, Empty } from 'antd';
import { TeamOutlined, UserOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import type { ColumnsType } from 'antd/es/table';

interface DepartmentStorage {
  department: string;
  totalSizeGB: number;
  fileCount: number;
  percentOfTotal: number;
  breakdown: {
    photoSizeGB: number;
    videoSizeGB: number;
    documentSizeGB: number;
  };
}

interface UserStorage {
  userId: string;
  userName: string;
  userEmail: string | null;
  department: string;
  totalSizeGB: number;
  fileCount: number;
  photoCount: number;
  videoCount: number;
}

function LoadingState({ tip, minHeight = 80 }: { tip: string; minHeight?: number }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <Spin tip={tip}>
        <div style={{ minHeight }} />
      </Spin>
    </div>
  );
}

/**
 * 部署別テーブル
 */
function DepartmentTable() {
  const { data, isLoading } = trpc.storage.getDepartmentReport.useQuery();

  const columns: ColumnsType<DepartmentStorage> = [
    {
      title: '部署名',
      dataIndex: 'department',
      key: 'department',
      width: '25%',
    },
    {
      title: '使用容量',
      dataIndex: 'totalSizeGB',
      key: 'totalSize',
      width: '35%',
      render: (sizeGB: number, record: DepartmentStorage) => (
        <div>
          <Progress
            percent={record.percentOfTotal}
            size="small"
            format={() => `${sizeGB.toFixed(1)} GB`}
            strokeColor="#1890ff"
          />
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            全体の {record.percentOfTotal.toFixed(1)}%
          </div>
        </div>
      ),
    },
    {
      title: 'ファイル数',
      dataIndex: 'fileCount',
      key: 'fileCount',
      width: '15%',
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: '内訳',
      key: 'breakdown',
      width: '25%',
      render: (_, record: DepartmentStorage) => (
        <div style={{ fontSize: 12 }}>
          <div>📷 写真: {record.breakdown.photoSizeGB.toFixed(1)} GB</div>
          <div>🎥 動画: {record.breakdown.videoSizeGB.toFixed(1)} GB</div>
          <div>📄 文書: {record.breakdown.documentSizeGB.toFixed(1)} GB</div>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <LoadingState tip="読み込み中..." />;
  }

  if (!data || data.length === 0) {
    return <Empty description="データがありません" />;
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="department"
      pagination={false}
      size="middle"
    />
  );
}

/**
 * ユーザー別テーブル
 */
function UserTable() {
  const { data, isLoading } = trpc.storage.getUserReport.useQuery({ limit: 20 });

  const columns: ColumnsType<UserStorage> = [
    {
      title: 'ユーザー名',
      dataIndex: 'userName',
      key: 'userName',
      width: '20%',
      render: (name: string, record: UserStorage) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.userEmail}</div>
        </div>
      ),
    },
    {
      title: '部署',
      dataIndex: 'department',
      key: 'department',
      width: '15%',
    },
    {
      title: '使用容量',
      dataIndex: 'totalSizeGB',
      key: 'totalSize',
      width: '20%',
      render: (sizeGB: number) => (
        <div>
          <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
            {sizeGB.toFixed(1)} GB
          </div>
        </div>
      ),
    },
    {
      title: 'ファイル数',
      dataIndex: 'fileCount',
      key: 'fileCount',
      width: '15%',
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: '内訳',
      key: 'breakdown',
      width: '30%',
      render: (_, record: UserStorage) => (
        <div style={{ fontSize: 12 }}>
          <Tag>📷 {record.photoCount}枚</Tag>
          <Tag>🎥 {record.videoCount}本</Tag>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <LoadingState tip="読み込み中..." />;
  }

  if (!data || data.length === 0) {
    return <Empty description="データがありません" />;
  }

  return (
    <div>
      <div style={{ marginBottom: 12, color: '#666' }}>
        使用量の多い上位20ユーザー
      </div>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="userId"
        pagination={false}
        size="middle"
      />
    </div>
  );
}

/**
 * アーカイブ推奨リスト
 */
function ArchiveOpportunitiesCard() {
  const { data, isLoading } = trpc.storage.getArchiveOpportunities.useQuery();

  if (isLoading) {
    return <LoadingState tip="読み込み中..." minHeight={120} />;
  }

  if (!data || data.opportunities.length === 0) {
    return (
      <Empty
        description="現在アーカイブ推奨ファイルはありません"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div>
      {data.opportunities.map((opp, index) => (
        <Card
          key={index}
          size="small"
          style={{
            marginBottom: 12,
            border: '1px solid #91d5ff',
            backgroundColor: '#f0f5ff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                {opp.category}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {opp.fileCount.toLocaleString()}件 ・ {opp.totalSizeGB.toFixed(1)} GB
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: '#666' }}>月額コスト削減</div>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                ¥{opp.potentialMonthlySavingJPY.toLocaleString()}
              </div>
            </div>
          </div>
        </Card>
      ))}

      {data.totalPotentialSavingJPY > 0 && (
        <Card
          style={{
            backgroundColor: '#f6ffed',
            border: '2px solid #b7eb8f',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
              総削減可能額（月額）
            </div>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: '#52c41a' }}>
              ¥{data.totalPotentialSavingJPY.toLocaleString()}
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              年間では約¥{(data.totalPotentialSavingJPY * 12).toLocaleString()}の削減が可能
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * 組織別ストレージ使用量ビュー
 */
export function OrganizationStorageView() {
  return (
    <div>
      {/* アーカイブ推奨 */}
      <Card
        title="💡 アーカイブ推奨"
        style={{ marginBottom: 16 }}
      >
        <ArchiveOpportunitiesCard />
      </Card>

      {/* 部署別・ユーザー別タブ */}
      <Card>
        <Tabs
          items={[
            {
              key: 'department',
              label: (
                <span>
                  <TeamOutlined />
                  部署別使用量
                </span>
              ),
              children: <DepartmentTable />,
            },
            {
              key: 'user',
              label: (
                <span>
                  <UserOutlined />
                  ユーザー別使用量
                </span>
              ),
              children: <UserTable />,
            },
          ]}
        />
      </Card>
    </div>
  );
}
